const CONTENT_KINDS = new Set([
  "question",
  "section_field",
  "authentication",
  "service_area",
  "treatment_options",
  "screening_intro",
  "consent",
  "lab",
  "product",
  "shipping",
  "checkout",
  "completion",
]);

const CONTAINER_KINDS = new Set([
  "section",
  "program_screening",
  "intake",
  "treatment_options",
  "consents",
  "labs",
  "treatment_selection",
  "checkout",
  "system",
]);

const isObject = (value) => (
  value !== null && typeof value === "object" && !Array.isArray(value)
);

const isPositiveInteger = (value) => Number.isInteger(value) && value > 0;

const validProgress = (progress) => {
  if (!isObject(progress)) return false;
  if (progress.mode === "indeterminate") return true;
  if (progress.mode === "phase_relative") {
    const phase = progress.phase_progress;
    return phase === undefined
      || (
        isObject(phase)
        && isPositiveInteger(phase.current)
        && isPositiveInteger(phase.total)
        && phase.current <= phase.total
      );
  }
  return progress.mode === "exact"
    && isPositiveInteger(progress.current)
    && isPositiveInteger(progress.total)
    && progress.current <= progress.total;
};

const validLocalProgress = (progress) => {
  if (!isObject(progress)) return false;
  if (progress.mode === "none") return true;
  if (progress.mode === "ordinal") {
    return isPositiveInteger(progress.current)
      && isPositiveInteger(progress.total)
      && progress.current <= progress.total;
  }
  return progress.mode === "remaining"
    && Number.isInteger(progress.remaining)
    && progress.remaining >= 0;
};

const validContainer = (container) => {
  if (!isObject(container) || !CONTAINER_KINDS.has(container.kind)) return false;
  if (typeof container.display_name !== "string" || !container.display_name.trim()) {
    return false;
  }
  if (container.kind === "section") {
    return typeof container.source_id === "string"
      && Boolean(container.source_id)
      && isPositiveInteger(container.version)
      && typeof container.occurrence_id === "string"
      && Boolean(container.occurrence_id);
  }
  if (["program_screening", "intake"].includes(container.kind)) {
    return container.version === undefined;
  }
  return container.version === undefined && container.occurrence_id === undefined;
};

export const safeRuntimePresentation = () => ({
  presentation_contract_version: "1",
  journey_presentation: {
    kind: "program",
    phase: "intake",
    progress: { mode: "indeterminate" },
  },
  presentation: {
    owner: { kind: "system", source_id: "treatment-flow", release_id: "" },
    container: { kind: "system", display_name: "Treatment" },
    local_progress: { mode: "none" },
    content: { kind: "question" },
  },
});

export const normalizeRuntimePresentation = (value) => {
  if (!isObject(value) || value.presentation_contract_version !== "1") return null;
  const journey = value.journey_presentation;
  const presentation = value.presentation;
  const owner = presentation?.owner;
  const content = presentation?.content;
  if (!isObject(journey) || !["program", "custom_program"].includes(journey.kind)) return null;
  if (typeof journey.phase !== "string" || !validProgress(journey.progress)) return null;
  if (!isObject(owner) || !["program", "custom_program", "system"].includes(owner.kind)) return null;
  if (typeof owner.source_id !== "string" || !owner.source_id) return null;
  if (typeof owner.release_id !== "string") return null;
  if (!validContainer(presentation?.container)) return null;
  if (!validLocalProgress(presentation.local_progress)) return null;
  if (!isObject(content) || !CONTENT_KINDS.has(content.kind)) return null;
  return value;
};

export const presentationForRuntime = (value) => {
  const normalized = normalizeRuntimePresentation(value);
  if (normalized) return normalized;
  if (process.env.NODE_ENV === "development") {
    console.debug("[TreatmentRuntime] presentation contract v1 unavailable; using safe fallback");
  }
  return safeRuntimePresentation();
};

export const presentationForRuntimeQuestion = ({
  presentation,
  question,
}) => {
  const runtime = presentationForRuntime(presentation);
  if (question?.question_type !== "treatment_screening_intro") return runtime;
  return {
    ...runtime,
    presentation: {
      ...runtime.presentation,
      container: { kind: "system", display_name: "Treatment" },
      content: { kind: "screening_intro" },
    },
  };
};

const questionIdentityCandidates = (question) => (
  [question?.release_node_id, question?.id, question?.source_id]
    .filter((value) => value !== undefined && value !== null && value !== "")
    .map(String)
);

const sameRuntimeQuestion = (left, right) => {
  const leftIdentities = questionIdentityCandidates(left);
  const rightIdentities = new Set(questionIdentityCandidates(right));
  return leftIdentities.some((identity) => rightIdentities.has(identity));
};

/**
 * Consent pages are traversed locally after the runtime projection enters the
 * consent state. Their API presentation is therefore not refreshed for every
 * consent, so derive the ordinal from the questions currently in that state.
 */
export const localProgressForRuntimeQuestion = ({
  presentation,
  currentQuestion,
  visibleQuestions = [],
}) => {
  const fallback = presentation?.presentation?.local_progress || { mode: "none" };
  if (
    presentation?.presentation?.container?.kind !== "consents"
    || currentQuestion?.question_type !== "consent"
    || !Array.isArray(visibleQuestions)
  ) {
    return fallback;
  }

  const consentQuestions = visibleQuestions.filter(
    (question) => question?.question_type === "consent",
  );
  const currentIndex = consentQuestions.findIndex((question) => (
    sameRuntimeQuestion(question, currentQuestion)
  ));
  if (currentIndex < 0 || consentQuestions.length === 0) return fallback;

  return {
    mode: "ordinal",
    current: currentIndex + 1,
    total: consentQuestions.length,
  };
};

export const runtimeProgressBarState = (progress) => {
  const values = progress?.mode === "exact"
    ? progress
    : progress?.mode === "phase_relative"
      ? progress.phase_progress
      : null;
  if (
    isPositiveInteger(values?.current)
    && isPositiveInteger(values?.total)
    && values.current <= values.total
  ) {
    return {
      percent: (values.current / values.total) * 100,
      indeterminate: false,
    };
  }
  return { percent: 0, indeterminate: false };
};

export { CONTENT_KINDS, CONTAINER_KINDS };
